import { type FC } from "react";

import { type IMaxHitRange } from "@/app-utils/blast.ts";
import { type BlastResult } from "@/graphql.ts";
import { BEST_HIT_COLUMN_WIDTH, SUBROW_HEIGHT } from "@/settings/settings";

export interface IHitGraphicsProps {
  maxHitRange: IMaxHitRange;
  queryProteinLength: number;
  blastResult: BlastResult;
  width?: number;
}

const HitGraphics: FC<IHitGraphicsProps> = (props) => {
  const { queryProteinLength, blastResult, maxHitRange, width } = props;

  if (!blastResult) {
    return null;
  }

  // image dimensions
  const strokeWidth = 2;
  const imgPixelWidth: number = width || BEST_HIT_COLUMN_WIDTH - 10;
  const imxPixelHeight = 13;
  const imgPixelNetWidth: number = imgPixelWidth - strokeWidth - 1;
  const imxPixelNetHeight: number = imxPixelHeight - strokeWidth;
  const imgPixelHalfHeight: number = imxPixelNetHeight / 2;

  // the image width is the same for all best hits of one protein and must fit all query/target sequences
  const imgAaWidth: number = maxHitRange.maxLeft + maxHitRange.maxRight + queryProteinLength;
  const scaleFactor: number = imgPixelNetWidth / imgAaWidth; // pixels per aa

  // the offset is the white space which has to be applied to the left of the current sequence
  const aaOffset: number = maxHitRange.maxLeft - (blastResult.targetStart - blastResult.queryStart);
  const pixelOffset: number = aaOffset * scaleFactor;

  // bar and line dimensions
  const barAaWidth: number = blastResult.queryStop - blastResult.queryStart + 1;
  const barPixelWidth: number = barAaWidth * scaleFactor;
  const leftSpaceAaWidth: number = blastResult.targetStart - 1;
  const rightSpaceAaWidth: number = blastResult.protein.length - blastResult.targetStop;
  const leftSpacePixelWidth: number = leftSpaceAaWidth * scaleFactor;
  const rightSpacePixelWidth: number = rightSpaceAaWidth * scaleFactor;

  const querySeqAaOffset: number = maxHitRange.maxLeft;
  const querySeqPixelOffset: number = querySeqAaOffset * scaleFactor;
  const querySeqPixelWidth: number = queryProteinLength * scaleFactor;

  return (
    <div style={{ width: "auto", display: "inline" }}>
      <svg className="best-hit-graphics" width={imgPixelWidth} height={SUBROW_HEIGHT}>
        <g transform={`translate(${strokeWidth}, ${strokeWidth})`}>
          <g transform={`translate(${querySeqPixelOffset}, 0)`} className="best-hit-graphics-query-area">
            <rect width={querySeqPixelWidth} height={imxPixelNetHeight}>
              <title>{"Query sequence range"}</title>
            </rect>
          </g>

          <g transform={`translate(${pixelOffset}, 0)`}>
            <g transform={`translate(${leftSpacePixelWidth}, 0)`}>
              <rect width={barPixelWidth} height={imxPixelNetHeight}>
                <title>{"Hit match range"}</title>
              </rect>
            </g>
            <g>
              <line x1={0} y1={imgPixelHalfHeight} x2={leftSpacePixelWidth} y2={imgPixelHalfHeight}>
                <title>{"Hit mismatch range"}</title>
              </line>
            </g>
            <g transform={`translate(${leftSpacePixelWidth + barPixelWidth}, 0)`}>
              <line x1={0} y1={imgPixelHalfHeight} x2={rightSpacePixelWidth} y2={imgPixelHalfHeight}>
                <title>{"Hit mismatch range"}</title>
              </line>
            </g>
          </g>
        </g>
      </svg>
    </div>
  );
};

export default HitGraphics;
